import { useState, useEffect, useMemo } from 'react';
import EverTrue from 'app';
import { useFluxStore } from '@evertrue/et-flux';
import $ from 'jquery';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useGate } from 'components/is-gated';
import { useIdentity } from 'base/identity-resolver';

// Import sources
import ListSource from 'apps/lists/sources/list-source';
import ListContactSource from 'apps/lists/sources/list-contact-source';
import SelectedContactsSource from 'apps/contact/sources/selected-contacts-source';
import MapSource from 'apps/map/sources/map-source';
import ListActivitySource from 'apps/lists/sources/list-activity-source';
import UrlSource from 'apps/layout/sources/url-source';

// Import stores
import ListStore from 'apps/lists/stores/list-store';
import ListContactStore from 'apps/lists/stores/list-contact-store';
import ListActivityStore from 'apps/lists/stores/list-activity-store';
import SelectedContactsStore from 'apps/contact/stores/selected-contacts-store';
import BulkAddStore from 'apps/layout/stores/bulk-add-store';
import FilterStore from 'apps/filters/stores/filter-store';
import ContactQueryStore from 'apps/contact/stores/contact-query-store';
import MapStore from 'apps/map/stores/map-store';

// Import components
import EmptyStateMessage from 'components/lists/empty-state-message';
import ContactTableToolbar from 'apps/contact/components/contacts/table/contact-table-toolbar';
import ContactFixedTable from 'apps/contact/components/contacts/contact-fixed-table';
import ListActivity from 'apps/lists/components/list-activity';
import ListMapController from 'apps/lists/controllers/list-map-controller';
import MapContacts from 'apps/map/controllers/map-contacts-controller';
import ShowHideAnimate from 'components/show-hide-animate';
import ProgressBar from 'components/forms/progress-bar';
import { LinkButton, Button, Icon } from '@evertrue/et-components';
import BulkListController from 'apps/lists/controllers/bulk-list-controller';
import useEmmaListFetcher from '../hooks/use-emma-list-fetcher';
import ListHeaderController from './list-header-controller';
import SortUtils from 'mixins/sort-utils';
import { buildListExportQuery } from '../utils/build-list-export-query';

const listStoreMap = (listId) => ({
  list: ListStore.getList(listId),
  listLoading: ListStore.getLoading(),
  previousList: ListStore.getLastSelectedList(),
});

const listContactStoreMap = (listId) => ({
  listContacts: ListContactStore.getContacts(listId),
  loading: ListContactStore.getLoading(),
});

const listActivityStoreMap = () => ({
  listActivityCollapsed: ListActivityStore.getCollapsed(),
});

const selectedContactsStoreMap = () => ({
  selectedContacts: SelectedContactsStore.getSelectedContacts(),
  showSelectedIndicator: SelectedContactsStore.getShowSelectedIndicator(),
  isAllSelected: SelectedContactsStore.getIsAllSelected(),
});

const bulkAddStoreMap = () => ({
  bullAddStatusObj: BulkAddStore.getBulkAddToList(),
});

// filters maps
const filterStoreMap = () => ({
  loadingFilters: FilterStore.getLoading(),
  filterParams: FilterStore.getActiveFiltersQuery(),
});

const contactQueryStoreMap = () => ({
  contacts: ContactQueryStore.getContacts(),
  loadingQueryContacts: ContactQueryStore.getLoading(),
});

const ListController = ({ listId, view = 'table', listContactIds = [], listContactIdQuery = null }) => {
  const [showModal, setShowModal] = useState(false);
  const [enrichmentLoading, setEnrichmentLoading] = useState(true);
  const [totalContacts, setTotalContacts] = useState(0);
  const [contactsForTable, setContactsForTable] = useState([]);
  const [queryForListCreation, setQueryForListCreation] = useState({});
  const { user } = useIdentity();
  const [hasEmma] = useGate('emma');
  const { accounts, groups, refresh } = useEmmaListFetcher(listId, hasEmma);

  const { list, listLoading, previousList } = useFluxStore(ListStore, () => listStoreMap(listId));
  const { listContacts, loading } = useFluxStore(ListContactStore, () => listContactStoreMap(listId));
  const { listActivityCollapsed } = useFluxStore(ListActivityStore, listActivityStoreMap);
  const { selectedContacts, showSelectedIndicator, isAllSelected } = useFluxStore(
    SelectedContactsStore,
    selectedContactsStoreMap
  );
  const { bullAddStatusObj } = useFluxStore(BulkAddStore, bulkAddStoreMap);
  const { loadingFilters, filterParams } = useFluxStore(FilterStore, filterStoreMap);
  const { contacts, loadingQueryContacts } = useFluxStore(ContactQueryStore, contactQueryStoreMap);

  const hasActiveFilters = filterParams && Object.keys(filterParams).length > 0;

  useEffect(() => {
    if (previousList !== listId) MapSource.setBoundingBox();
  }, [previousList, listId]);

  useEffect(() => {
    ListSource.select(listId);
    ListContactSource.filter(listId);
    setTimeout(() => ListContactSource.setEvent('list_action'), 0);
    return () => {
      ListSource.select(null);
      SelectedContactsSource.clear();
      MapSource.clearClusters();
    };
  }, [listId]);

  useEffect(() => {
    if (hasActiveFilters) {
      if (!loadingFilters && !loadingQueryContacts) {
        setContactsForTable(contacts);
        setTotalContacts(contacts?.total ?? 0);
      } else {
        // To maintain correct count and contacts, fall back to listContacts while filters/query contacts are still loading
        setContactsForTable(listContacts);
        setTotalContacts(listContacts?.total ?? 0);
      }
    } else if (!loading) {
      setContactsForTable(listContacts);
      setTotalContacts(listContacts?.total ?? 0);
    }
  }, [hasActiveFilters, contacts, listContacts, loadingFilters, loading, loadingQueryContacts]);

  useEffect(() => {
    const query = buildListExportQuery({ filterParams, listContactIdQuery, listId });
    setQueryForListCreation(query);
  }, [filterParams, listContactIdQuery, listId]);

  const handleSaveListName = (name) => {
    if (name && name !== list?.name) {
      ListSource.update({
        ...list,
        name,
        type: 'user_list',
      });
      EverTrue.track.set('list_action', { type: 'update_name' });
    }
  };

  const handleRemove = () => {
    if (isAllSelected && !hasActiveFilters) {
      ListContactSource.removeContacts(listId, listContactIds);
    } else {
      ListContactSource.removeContacts(listId, selectedContacts);
    }
    if (!listActivityCollapsed) {
      ListActivitySource.collapse(!listActivityCollapsed);
    }
    handleClearSelections();
  };

  const handleSort = (sort_key, reverse, silent) => {
    if (hasActiveFilters) {
      let sortParamQuery;
      if (sort_key === 'proximity_sort' || sort_key === 'addresses.location') {
        sortParamQuery = MapStore.getProximitySort(reverse);
      } else {
        sortParamQuery = SortUtils.getQuery(sort_key, reverse)[0];
      }
      UrlSource.updateSort(sortParamQuery, silent);
    } else {
      ListContactSource.changeSort(listId, sort_key, !!reverse, silent);
    }

    SelectedContactsSource.clear();
  };

  const handlePage = (page) => {
    if (hasActiveFilters) {
      UrlSource.updatePage(page);
    } else {
      ListContactSource.changePage(listId, page);
    }

    SelectedContactsSource.clear();
    setEnrichmentLoading(true);
  };

  const handleCollapseActivity = () => {
    if (listActivityCollapsed) {
      EverTrue.track.set('list_action', { type: 'list_activity_feed' });
    }
    ListActivitySource.collapse(!listActivityCollapsed);
    // Trigger resize to re-calculate the table columns & scrolling
    setTimeout(() => {
      setTimeout(() => $(window).trigger('resize'), 500);
    }, 0);
  };

  const handleCreateList = (name) => {
    const payload = { name, type: 'user_list' };

    if (isAllSelected) {
      const contactData = hasActiveFilters ? queryForListCreation : listContactIds;
      ListSource.bulkAddToListCreate(payload, totalContacts, contactData);
    } else {
      ListSource.create(payload, selectedContacts);
    }
    handleClearSelections();
  };

  const handleClearSelections = () => {
    // Clear all selected once a bulk add is fired
    SelectedContactsSource.toggleSelectedIndicator(false);
    SelectedContactsSource.selectAll(false);
    SelectedContactsSource.clear();
  };

  const renderPanelToggle = () => {
    return (
      <LinkButton
        title="Toggle list activity"
        className={classNames('list-detail--activity-toggle', {
          'is-collapsed': listActivityCollapsed,
        })}
        onClick={handleCollapseActivity}
      >
        <Icon icon="collapse" size={2} rotate={!listActivityCollapsed ? 180 : 0} />
        {listActivityCollapsed && <span className="list-detail--activity-toggle-label">Show Activity</span>}
      </LinkButton>
    );
  };

  const isOwner = useMemo(() => list?.user_id === user?.id, [list?.user_id, user?.id]);

  const progressValue = useMemo(() => {
    const val = (bullAddStatusObj?.progress ?? 0) * 100;
    return val === 0 ? 0.03 : val;
  }, [bullAddStatusObj?.progress]);

  const isBulkListInProgress = useMemo(() => {
    const progress = bullAddStatusObj?.progress;
    return bullAddStatusObj?.id === Number(listId) && typeof progress === 'number' && progress !== 1;
  }, [bullAddStatusObj, listId]);

  return (
    <div className="list-detail fixed-page-wrapper" style={{ top: 72 }}>
      {!listLoading && !list ? (
        <EmptyStateMessage icon="lock" text="You do not have access to this list" />
      ) : isBulkListInProgress ? (
        <EmptyStateMessage icon="clipboard-text" text="Adding constituents to list...">
          <div className="list-detail--progress-bar">
            <ProgressBar className="progress-bar-info-inverse" percent={progressValue} />
          </div>
        </EmptyStateMessage>
      ) : (
        <div>
          <ListHeaderController
            list={list}
            isOwner={isOwner}
            listContacts={hasActiveFilters ? contactsForTable : listContacts}
            selectedContacts={selectedContacts}
            groups={groups}
            accounts={accounts}
            handleSaveListName={handleSaveListName}
            handleRemove={handleRemove}
            refresh={refresh}
            listId={listId}
            handleCreateList={handleCreateList}
            loading={loading || loadingQueryContacts || loadingFilters}
            isAllSelected={isAllSelected}
            hasActiveFilters={hasActiveFilters}
            filterParams={queryForListCreation}
            listContactIds={listContactIds}
          />

          <div className="list-detail--content fixed-page-wrapper">
            <ContactTableToolbar
              paginationClassName="list-toolbar-pagination"
              contacts={hasActiveFilters ? contactsForTable : listContacts}
              listActionLabel="Add to another list"
              onPage={handlePage}
              allowAddAll={true}
              filterParams={queryForListCreation}
              totalResultsCount={hasActiveFilters ? totalContacts : listContacts?.total}
              totalNumber={hasActiveFilters ? totalContacts : listContacts?.total}
            >
              <Button type="destroy" disabled={!selectedContacts?.length} onClick={handleRemove}>
                Remove from list
              </Button>

              <Button
                type="simple"
                onClick={() => EverTrue.Navigator(view === 'table' ? `/list/${listId}/map` : `/list/${listId}`, true)}
              >
                <Icon icon={view === 'table' ? 'map gt-icon-lg' : 'table-large gt-icon-lg'} />
                {view === 'table' ? 'Map' : 'Table'}
              </Button>
            </ContactTableToolbar>

            <div className="list-detail--body fixed-page-wrapper" style={{ top: showSelectedIndicator ? 85 : 72 }}>
              {showModal && <BulkListController list={list} remove={() => setShowModal(false)} />}

              {view === 'map' ? (
                <>
                  <MapContacts contacts={listContacts} loading={loading} onSort={handleSort} />
                  <ListMapController list_id={listId} />
                </>
              ) : (
                <>
                  <ContactFixedTable
                    className={classNames('list-detail--contact-table', {
                      'is-activity-collapsed': listActivityCollapsed,
                    })}
                    contacts={hasActiveFilters ? contactsForTable : listContacts}
                    loading={loading || loadingQueryContacts || loadingFilters}
                    onPage={handlePage}
                    onSort={handleSort}
                    emptyStateMessage={
                      hasActiveFilters
                        ? 'No results match your current filters'
                        : 'No constituents have been added to this list.'
                    }
                    actionLabel={hasActiveFilters ? '' : '+ Add Constiutents'}
                    onAction={() => setShowModal(true)}
                    setEnrichmentLoading={setEnrichmentLoading}
                    enrichmentLoading={enrichmentLoading}
                    topPosition={showSelectedIndicator ? 20 : null}
                  />

                  <ShowHideAnimate
                    show={listActivityCollapsed}
                    className="list-detail--activity-panel is-collapsed"
                    animateInClass="slideInRight"
                    animateOutClass="slideOutRight"
                  >
                    {renderPanelToggle()}
                  </ShowHideAnimate>

                  <ShowHideAnimate
                    show={!listActivityCollapsed}
                    className="list-detail--activity-panel"
                    animateInClass="slideInRight"
                    animateOutClass="slideOutRight"
                  >
                    <div className="list-detail--activity">
                      {renderPanelToggle()}
                      <ListActivity listId={listId} />
                    </div>
                  </ShowHideAnimate>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ListController.propTypes = {
  listId: PropTypes.any.isRequired,
  view: PropTypes.oneOf(['table', 'map']),
  listContactIds: PropTypes.array,
  listContactIdQuery: PropTypes.object,
};

export default ListController;
